'use client';
import React, { useCallback, useEffect, useState, useRef } from 'react';
import { Transition } from '@headlessui/react';
import { useUserCountry, UseUserCountryReturn } from '@/utils/useUserCountry';
import { Skeleton } from '@/components/ui/skeleton';
import { Play } from 'iconsax-react';
import { YouTubeEmbed } from '@next/third-parties/google';
interface VideoComponentProps {
    aparatHnembed?: string;
    youtubeHnembed?: string;
    classNameCustom?: string;
    /** When one of the two embeds is empty, show the other one to everybody (off by default). */
    fallback?: boolean;
}

const VideoComponent: React.FC<VideoComponentProps> = ({
    aparatHnembed,
    youtubeHnembed,
    classNameCustom,
    fallback = false,
}) => {
    const { userCountry }: UseUserCountryReturn = useUserCountry();

    // Aparat for Iran, YouTube elsewhere; with `fallback` the filled one stands in for the empty one.
    const inIran = userCountry === 'IR';
    const mode: 'aparat' | 'youtube' | null = inIran
        ? aparatHnembed
            ? 'aparat'
            : fallback && youtubeHnembed
              ? 'youtube'
              : null
        : youtubeHnembed
          ? 'youtube'
          : fallback && aparatHnembed
            ? 'aparat'
            : null;

    const [isInView, setIsInView] = useState<boolean>(false);
    const [isVideoLoaded, setIsVideoLoaded] = useState<boolean>(false);
    const [videoContainerElement, setVideoContainerElement] = useState<HTMLDivElement | null>(null);

    const aparatContainerRef = useRef<HTMLDivElement | null>(null);

    const handleIntersection = useCallback((entries: IntersectionObserverEntry[]) => {
        const entry = entries[0];
        if (entry.isIntersecting) {
            setIsInView(true);
        }
    }, []);

    useEffect(() => {
        const observer = new IntersectionObserver(handleIntersection, {
            root: null,
            threshold: 0.1, // Adjust as necessary
        });

        if (videoContainerElement) {
            observer.observe(videoContainerElement);
        }

        return () => {
            if (videoContainerElement) {
                observer.unobserve(videoContainerElement);
            }
        };
    }, [handleIntersection, videoContainerElement]);

    useEffect(() => {
        if (isInView && mode === 'aparat' && aparatHnembed) {
            const aparatEmbedHTML = aparatHnembed;
            const parser = new DOMParser();
            const doc = parser.parseFromString(aparatEmbedHTML, 'text/html');
            const scriptTag = doc.querySelector('script');
            const src = scriptTag?.getAttribute('src') || '';
            const divId = doc.querySelector('div')?.id || '';

            if (src && divId && aparatContainerRef.current) {
                const containerDiv = document.createElement('div');
                containerDiv.id = divId;
                const script = document.createElement('script');
                script.type = 'text/javascript';
                script.src = src;
                script.async = true;

                containerDiv.appendChild(script);
                aparatContainerRef.current.appendChild(containerDiv);

                script.onload = () => {
                    setIsVideoLoaded(true);
                };

                script.onerror = () => {
                    console.error(`Failed to load script: ${src}`);
                    setIsVideoLoaded(false);
                };
            }
        }
    }, [isInView, mode, aparatHnembed]);

    const handleIframeLoad = () => {
        setIsVideoLoaded(true);
    };

    const renderVideo = (): React.ReactNode => {
        if (mode === 'youtube' && youtubeHnembed) {
            const embedHtml = youtubeHnembed;
            const urlMatch = embedHtml.match(/src="([^"]+)"/);
            let videoId = '';

            if (urlMatch && urlMatch[1]) {
                const url = urlMatch[1];
                const idMatch = url.match(/embed\/([a-zA-Z0-9_-]+)/);
                if (idMatch && idMatch[1]) {
                    videoId = idMatch[1];
                }
            }

            return (
                <div
                    className='flex aspect-video justify-center w-full youtubeVideo'
                    onLoad={handleIframeLoad}
                >
                    <YouTubeEmbed
                        videoid={videoId}
                        width={1200}
                        style={'max-width:100%;border-radius:5px;width:100%'}
                        params='controls=1'
                    />
                </div>
            );
        } else if (mode === 'aparat' && aparatHnembed) {
            return <div className='aspect-video h-full rounded-lg' ref={aparatContainerRef} />;
        }
        return null;
    };

    return (
        <div
            ref={(el) => setVideoContainerElement(el)} // Callback ref to capture the element
            className={`lg:mb-5 relative w-full overflow-y-hidden ${classNameCustom}`}
        >
            {!isVideoLoaded && (
                <div className='absolute inset-0 flex flex-col items-center justify-center bg-gray-300 dark:bg-white/5 bg-opacity-50 rounded-lg'>
                    <Skeleton className='h-auto bg-gray-200 border-primary/10 border' />
                    <Play
                        size={25}
                        className={
                            'fill-white bg-black/70 p-4 box-content rounded-full absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 animate-pulse delay-300'
                        }
                    />
                </div>
            )}
            {isInView && (
                <Transition
                    as='div'
                    show={isInView}
                    enter='transition-opacity duration-500'
                    enterFrom='opacity-0'
                    enterTo='opacity-100'
                >
                    {renderVideo()}
                </Transition>
            )}
        </div>
    );
};

export default React.memo(VideoComponent);
